import { useMemo, forwardRef } from "react";
import { bool, func, node, number, oneOf, string, object } from "prop-types";
import { observer } from "mobx-react";
import { getTimelineBarWeight } from "@tools/EstimateEditor/utils";
import { ButtonBase, Grid, IconButton } from "@material-ui/core";
import { ExpandMore } from "@material-ui/icons";
import useStyle from "./TimelineChartBar.style";
import classnames from "classnames";

export const TimelineChartBar =  observer(forwardRef(({
  color,
  fontColor,
  weight,
  exceeds,
  height: heightMode = "medium",
  barLeft,
  barWidth,
  label,
  children,
  labelActions,
  hideLabel,
  draggable,
  expandable,
  expanded,
  onExpand,
  onClick,
  animate,
  className,
  style,
  expandOnContainerClick,
}, ref) => {
  const classes = useStyle();

  const useButtonComponent = Boolean(onClick || expandOnContainerClick);

  const BarContainer = useButtonComponent ? ButtonBase : Grid;

  const height = useMemo(() => {
    if (heightMode === "large") return 40;
    if (heightMode === "medium") return 32;
    if (heightMode === "small") return 24;
  }, [ heightMode ]);

  const handleExpand = (event) => {
    event.stopPropagation();
    onExpand?.(event);
  }

  const handleContainerClick = (event) => {
    if(expandOnContainerClick)
      return handleExpand(event);
    onClick?.(event);
  }
  
  return (
    <BarContainer
      ref={ref}
      component="div"
      {...(!useButtonComponent ? { item: true, container: true } : {})}
      onClick={useButtonComponent ? handleContainerClick : undefined}
      style={{
        ...style,
        height,
        ...(typeof barWidth === "number" ? { minWidth: barWidth - 4 } : {}),
        ...(typeof barLeft === "number" ? { left: barLeft } : {}),
      }}
      className={classnames(
        "tm-bar apply-events relative",
        draggable && "cursor-grab",
        useButtonComponent && "cursor-pointer",
        classes.container,
        animate && classes.animate,
        exceeds && classes.exceeds,
        className,
      )}
    >
      {
        barWidth > 0 &&
        <>
          <div
            className={classes.bar}
            style={{
              height,
              backgroundColor: color,
              ...(typeof weight === "number" ? { opacity: getTimelineBarWeight(weight) } : {}),
              ...(typeof barWidth === "number" ? { width: barWidth - 4 } : {}),
            }}
          />
          {label && !hideLabel && (
            <div
              className={classnames(classes.label, barWidth <= 16 && "px-0")}
              style={{
                color: fontColor,
                ...(typeof barWidth === "number" ? { maxWidth: barWidth - 4 } : {}),
              }}
            >
              {
                expandable &&
                <IconButton
                  size="small"
                  className="text-sm noDrag expand"
                  onClick={handleExpand}
                >
                  <ExpandMore
                    className={classnames("transition", expanded ? "rotate-180" : "rotate-0")}
                  />
                </IconButton>
              }
              <span className={classes.labelText}>
            {label}
          </span>
              { labelActions }
            </div>
          )}
          { children }
        </>
      }
    </BarContainer>
  );
}));

TimelineChartBar.propTypes = {
  weight: number,
  height: oneOf(["large", "medium", "small", "weight"]),
  exceeds: bool,
  barLeft: number,
  barWidth: number,
  label: string,
  fontColor: string,
  draggable: bool,
  expandable: bool,
  expanded: bool,
  onExpand: func,
  onClick: func,
  className: string,
  color: string.isRequired,
  children: node,
  labelActions: node,
  hideLabel: bool,
  animate: bool,
  style: object,
};
